import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IDelta } from '@feathr/components';

import { getDeltaArrow, getDeltaClassName } from './KpiTiles.utils';

import * as styles from './KpiTiles.css';

export interface IDeltaIndicatorProps {
  delta: IDelta;
  /**
   * Optional secondary line rendered after the "vs previous period" copy
   * (e.g., an "Attributed via …" line on the value tile).
   */
  subtext?: ReactNode;
}

/**
 * Renders the delta arrow, percentage change, and "vs previous period" copy.
 * Returns `null` when there is no delta to display (e.g., prior period was empty).
 */
function DeltaIndicator({ delta, subtext }: Readonly<IDeltaIndicatorProps>): JSX.Element | null {
  const { t } = useTranslation();

  if (delta.direction === 'none') {
    return null;
  }

  const arrow = getDeltaArrow(delta.direction);
  const className = getDeltaClassName(delta.direction, styles);

  return (
    <p className={styles.delta}>
      <span className={className}>
        {arrow} {delta.pct.toFixed(1)}%
      </span>
      <span className={styles.deltaDescription}>
        {` ${t('vs previous period')}`}
        {subtext}
      </span>
    </p>
  );
}

export default DeltaIndicator;
